// Sprint 10 — admin Camera Management page.
//
// Table view of every camera in the tenant. "Add Camera" opens a slide-in
// drawer (rendered inline via Tailwind transforms — keeps the diff free of
// new Radix deps; full shadcn Sheet can drop in later as a no-op rename).
//
// On successful create, the page shows a one-time credentials card with the
// freshly-minted HLS URL + stream_key. That card never re-appears once
// dismissed; the API never returns stream_key/HLS again until the operator
// explicitly views a camera detail. (HLS URL is also cached on the camera
// row, so this is more about "here, copy it now" UX than a true one-time
// secret.)
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  CopyIcon,
  PlusIcon,
  RefreshCwIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";

import {
  useAdminCreateCamera,
  useAdminDeleteCamera,
  usePollCameraHealth,
  useCameras,
  type AdminCameraCreateInput,
  type AdminCameraCreateResponseT,
  type NVRHealthSnapshotT,
} from "@/api/cameras";
import { api } from "@/api/client";
import { useRecordingConfigs } from "@/api/recordings";
import { RecordingConfigDrawer } from "@/components/RecordingConfigDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";


function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard blocked — user can still select text manually */
        }
      }}
      className="inline-flex items-center gap-1 rounded border border-white/20 bg-white/5 px-2 py-1 text-xs text-white/90 transition hover:bg-white/10"
      aria-label="Copy"
    >
      {copied ? (
        <>
          <CheckCircle2 className="h-3 w-3" /> Copied
        </>
      ) : (
        <>
          <CopyIcon className="h-3 w-3" /> Copy
        </>
      )}
    </button>
  );
}


// ---------------------------------------------------------------------------
// One-time credentials card — rendered after a successful create.
// ---------------------------------------------------------------------------
function CredentialsCard({
  result,
  onAddAnother,
  onClose,
}: {
  result: AdminCameraCreateResponseT;
  onAddAnother: () => void;
  onClose: () => void;
}) {
  return (
    <Card className="border-[#ff6d00]/40 bg-primary text-white">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[#ff6d00]">
              Camera added — save this once.
            </p>
            <p className="mt-1 text-xs text-white/70">
              The HLS URL is also stored on the camera record, but copy now
              to skip a round-trip.
            </p>
          </div>
          {!result.mediamtx_registered && (
            <Badge variant="error">Streaming server registration failed — will retry on next API boot</Badge>
          )}
        </div>

        <div className="space-y-3 rounded border border-white/10 bg-black/30 p-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-white/50">Stream key</div>
            <div className="mt-1 flex items-center justify-between gap-2 font-mono text-sm">
              <span className="truncate">{result.stream_key}</span>
              <CopyButton value={result.stream_key} />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-white/50">HLS URL</div>
            <div className="mt-1 flex items-center justify-between gap-2 font-mono text-xs">
              <span className="truncate">{result.hls_url}</span>
              <CopyButton value={result.hls_url} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/live">Open Live View</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={onAddAnother}>
            Add another
          </Button>
          <Button size="sm" onClick={onClose}>Done</Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Add Camera Drawer — slide-in from the right.
// ---------------------------------------------------------------------------
function AddCameraDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (r: AdminCameraCreateResponseT) => void;
}) {
  const create = useAdminCreateCamera();
  const [form, setForm] = useState<AdminCameraCreateInput>({
    name: "",
    location: "",
    rtsp_url: "",
    audio_enabled: true,
    connection_mode: "direct_rtsp",
    onvif_port: 80,
  });
  const [healthOpen, setHealthOpen] = useState(false);

  function set<K extends keyof AdminCameraCreateInput>(k: K, v: AdminCameraCreateInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      // Don't send empty NVR fields — backend treats absence as
      // "health monitoring not configured".
      const payload: AdminCameraCreateInput = { ...form };
      if (!payload.onvif_host) {
        delete payload.onvif_host;
        delete payload.onvif_user;
        delete payload.onvif_pass;
      }
      const result = await create.mutateAsync(payload);
      onCreated(result);
      setForm({
        name: "",
        location: "",
        rtsp_url: "",
        audio_enabled: true,
        connection_mode: "direct_rtsp",
        onvif_port: 80,
      });
    } catch {
      /* error surfaces via create.error below */
    }
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "fixed right-0 top-0 z-50 h-full w-full max-w-md transform overflow-y-auto border-l border-border bg-background shadow-xl transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-lg font-semibold">Add Camera</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Close drawer"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6 p-4">
          {/* ---- Stream config ---- */}
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Stream Config
            </p>
            <div>
              <Label htmlFor="cam-name">Camera Name</Label>
              <Input
                id="cam-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                placeholder="e.g. Reception"
              />
            </div>
            <div>
              <Label htmlFor="cam-location">Location / Site</Label>
              <Input
                id="cam-location"
                value={form.location ?? ""}
                onChange={(e) => set("location", e.target.value)}
                placeholder="e.g. Mumbai HO — Ground Floor"
              />
            </div>
            <div>
              <Label htmlFor="cam-rtsp">RTSP URL</Label>
              <Input
                id="cam-rtsp"
                value={form.rtsp_url}
                onChange={(e) => set("rtsp_url", e.target.value)}
                required
                placeholder="rtsp://admin:password@203.0.113.10:554/Streaming/Channels/101"
                className="font-mono text-xs"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                The streaming server will pull from this URL on demand.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="cam-audio">Enable Audio</Label>
              <button
                id="cam-audio"
                type="button"
                role="switch"
                aria-checked={form.audio_enabled}
                onClick={() => set("audio_enabled", !form.audio_enabled)}
                className={cn(
                  "relative inline-flex h-5 w-9 items-center rounded-full transition",
                  form.audio_enabled ? "bg-[#ff6d00]" : "bg-muted",
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition",
                    form.audio_enabled ? "translate-x-4" : "translate-x-0.5",
                  )}
                />
              </button>
            </div>
            <div>
              <Label htmlFor="cam-mode">Connection Mode</Label>
              <select
                id="cam-mode"
                value={form.connection_mode}
                onChange={(e) =>
                  set("connection_mode", e.target.value as "direct_rtsp")
                }
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="direct_rtsp">Direct RTSP (active)</option>
                <option disabled>🔒 SLink Agent (coming soon)</option>
                <option disabled>🔒 On-Premise (coming soon)</option>
              </select>
            </div>
          </section>

          {/* ---- NVR Health (collapsible) ---- */}
          <section className="space-y-3 border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setHealthOpen((v) => !v)}
              className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <span>NVR Health (optional)</span>
              <span className="text-base">{healthOpen ? "−" : "+"}</span>
            </button>
            {healthOpen && (
              <div className="space-y-3">
                <p className="text-[11px] text-muted-foreground">
                  Used to poll the NVR's ISAPI endpoint for HDD status and
                  uptime. Hikvision NVRs at the same IP as the camera work
                  out of the box.
                </p>
                <div>
                  <Label htmlFor="onvif-host">NVR IP / Host</Label>
                  <Input
                    id="onvif-host"
                    value={form.onvif_host ?? ""}
                    onChange={(e) => set("onvif_host", e.target.value)}
                    placeholder="192.168.1.10"
                  />
                </div>
                <div>
                  <Label htmlFor="onvif-port">HTTP Port</Label>
                  <Input
                    id="onvif-port"
                    type="number"
                    value={form.onvif_port ?? 80}
                    onChange={(e) => set("onvif_port", Number(e.target.value) || 80)}
                  />
                </div>
                <div>
                  <Label htmlFor="onvif-user">ONVIF Username</Label>
                  <Input
                    id="onvif-user"
                    value={form.onvif_user ?? ""}
                    onChange={(e) => set("onvif_user", e.target.value)}
                    placeholder="admin"
                  />
                </div>
                <div>
                  <Label htmlFor="onvif-pass">ONVIF Password</Label>
                  <PasswordInput
                    id="onvif-pass"
                    value={form.onvif_pass ?? ""}
                    onChange={(e) => set("onvif_pass", e.target.value)}
                  />
                </div>
              </div>
            )}
          </section>

          {create.isError && (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              {(create.error as { response?: { data?: { detail?: string } }; message?: string })
                ?.response?.data?.detail ??
                (create.error as { message?: string })?.message ??
                "Could not create camera"}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
            disabled={create.isPending || !form.name || !form.rtsp_url}
          >
            {create.isPending ? "Adding…" : "Add Camera"}
          </Button>
        </form>
      </aside>
    </>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function AdminCamerasPage() {
  const { data, isLoading } = useCameras({ limit: 200 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [created, setCreated] = useState<AdminCameraCreateResponseT | null>(null);
  const [latestHealth, setLatestHealth] = useState<Record<string, NVRHealthSnapshotT | undefined>>({});
  const [recCfgCamera, setRecCfgCamera] = useState<{ id: string; name: string } | null>(null);
  const recConfigs = useRecordingConfigs();
  const recCfgByCam = (recConfigs.data?.items ?? []).reduce<
    Record<string, { enabled: boolean }>
  >((acc, c) => {
    acc[c.camera_id] = { enabled: c.enabled };
    return acc;
  }, {});
  const poll = usePollCameraHealth();
  const del = useAdminDeleteCamera();

  async function handlePollHealth(id: string) {
    // FIX C — POST now returns 202; the snapshot lands shortly via the
    // background task. Poll /health/latest until checked_at exceeds the
    // click timestamp (30s ceiling).
    const clickTs = Date.now();
    try {
      await poll.mutateAsync(id);
    } catch {
      return;
    }
    const deadline = clickTs + 30_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3_000));
      try {
        const { data } = await api.get<NVRHealthSnapshotT>(
          `/api/v1/cameras/${id}/health/latest`,
        );
        if (data && new Date(data.checked_at).getTime() > clickTs) {
          setLatestHealth((m) => ({ ...m, [id]: data }));
          return;
        }
      } catch {
        // 404 until first snapshot — keep trying.
      }
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete ${name}? Stream will stop and credentials will be invalidated.`)) return;
    await del.mutateAsync(id);
  }

  const cameras = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Admin · Cameras</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} configured` : "Loading…"}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportCsvButton
            rows={cameras}
            filename="cameras"
            columns={[
              { header: "Name", value: (c) => c.name },
              { header: "Status", value: (c) => c.status ?? "" },
              { header: "Connection mode", value: (c) => c.connection_mode ?? "" },
              { header: "Site", value: (c) => c.site_id ?? "" },
              { header: "Channel", value: (c) => (c as { channel_no?: number | null }).channel_no ?? "" },
              { header: "NVR model", value: (c) => (c as { nvr_model?: string | null }).nvr_model ?? "" },
              { header: "NVR serial", value: (c) => (c as { nvr_serial?: string | null }).nvr_serial ?? "" },
              { header: "Stream key", value: (c) => c.stream_key ?? "" },
            ]}
          />
          <Button
            onClick={() => {
              setCreated(null);
              setDrawerOpen(true);
            }}
            className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            Add Camera
          </Button>
        </div>
      </div>

      {created && (
        <CredentialsCard
          result={created}
          onAddAnother={() => {
            setCreated(null);
            setDrawerOpen(true);
          }}
          onClose={() => setCreated(null)}
        />
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Loading cameras…
            </div>
          ) : cameras.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No cameras yet — click <strong>Add Camera</strong> to register one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Mode</th>
                    <th className="px-4 py-3">NVR Model</th>
                    <th className="px-4 py-3">Channel</th>
                    <th className="px-4 py-3 font-mono">Stream Key</th>
                    <th className="px-4 py-3">Audio</th>
                    <th className="px-4 py-3">Recording</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Health</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {cameras.map((c) => {
                    const meta = (c.extra_metadata ?? {}) as Record<string, unknown>;
                    const streamKey = (c as { stream_key?: string }).stream_key
                      ?? (typeof meta.mediamtx_path === "string" ? meta.mediamtx_path : null);
                    const location = typeof meta.location === "string" ? meta.location : "—";
                    const isActive = (c as { is_active?: boolean }).is_active ?? true;
                    const audio = (c as { audio_enabled?: boolean }).audio_enabled ?? c.has_audio;
                    const health = latestHealth[c.id];
                    // Sprint-10-fix label mapping. Frontend's existing
                    // ConnectionMode type doesn't include the new
                    // 'direct_rtsp' / 'slink_agent' / 'onpremise' values;
                    // legacy 'agent' / 'direct' still round-trip.
                    const modeLabel = (
                      {
                        agent: "SLink Agent",
                        direct: "Direct (legacy)",
                        direct_rtsp: "Direct RTSP",
                        slink_agent: "SLink Agent",
                        onpremise: "On-Premise",
                      } as Record<string, string>
                    )[c.connection_mode] ?? c.connection_mode;
                    const nvrModel = (c as { nvr_model?: string | null }).nvr_model ?? "—";
                    const channelNo = (c as { channel_no?: number | null }).channel_no ?? "—";
                    return (
                      <tr key={c.id} className="hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">{c.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{location}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{modeLabel}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{nvrModel}</td>
                        <td className="px-4 py-3 text-xs">{channelNo}</td>
                        {/* T3d — an EMPTY stream_key is CORRECT for an
                            agent-push camera: the SLink agent publishes to
                            MediaMTX under a path the cloud derives, so there
                            is no key to register. Rendering "Not registered"
                            in alarm orange for all 15 cameras stated a fault
                            that does not exist — the emptiness was never the
                            defect, the styling was.

                            A direct/legacy camera with no key IS incomplete,
                            so that case keeps the warning. The two are
                            distinguished by connection_mode, which is the
                            fact that decides whether a key is expected. */}
                        <td className="px-4 py-3 font-mono text-xs">
                          {streamKey ?? (
                            c.connection_mode === "agent"
                            || c.connection_mode === "slink_agent"
                              ? (
                                <span
                                  className="text-muted-foreground"
                                  title="Agent-push cameras publish under an agent-derived MediaMTX path — no stream key is expected."
                                >
                                  n/a — agent-push
                                </span>
                              )
                              : <span className="text-[#ff6d00]">Not registered</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {audio ? <span className="text-emerald-600">on</span> : <span className="text-muted-foreground">off</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setRecCfgCamera({ id: c.id, name: c.name })}
                            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-muted"
                            title="Configure recording"
                          >
                            {recCfgByCam[c.id]?.enabled ? (
                              <Badge variant="error">● REC</Badge>
                            ) : (
                              <Badge variant="offline">⏸ Off</Badge>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={isActive ? "online" : "offline"}>
                            {isActive ? "active" : "inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "inline-block h-2 w-2 rounded-full",
                              health?.hdd_status === "ok"
                                ? "bg-emerald-500"
                                : health?.hdd_status === "warning"
                                  ? "bg-yellow-500"
                                  : health?.hdd_status === "error"
                                    ? "bg-red-500"
                                    : "bg-muted-foreground/30",
                            )}
                          />
                          <span className="ml-2 text-xs text-muted-foreground">
                            {health?.hdd_status ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handlePollHealth(c.id)}
                              disabled={poll.isPending}
                              className="rounded p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
                              title="Poll Health Now"
                            >
                              <RefreshCwIcon
                                className={cn(
                                  "h-3.5 w-3.5",
                                  poll.isPending && poll.variables === c.id && "animate-spin",
                                )}
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(c.id, c.name)}
                              disabled={del.isPending}
                              className="rounded p-1.5 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2Icon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddCameraDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(r) => {
          setDrawerOpen(false);
          setCreated(r);
        }}
      />

      <RecordingConfigDrawer
        cameraId={recCfgCamera?.id ?? null}
        cameraName={recCfgCamera?.name}
        open={recCfgCamera !== null}
        onOpenChange={(o) => {
          if (!o) setRecCfgCamera(null);
        }}
      />
    </div>
  );
}
